import {
    QueryFilters,
    useActiveResourcesPreview,
    useActiveUsersPreview,
    useAllAuthenticationsTraffic,
    useAuthenticationDenyRate,
    useAuthenticationsDenied,
    useAuthenticationTypeSummary,
    useBlockedResourcesPreview,
    useBlockedUsersPreview,
    useDenyPolicies,
    usePolicyType,
    useProtectedUsers,
    useProtocolTypeSummary,
    useRiskyIndicators,
    useRiskyResources,
    useRiskyUsers
} from '../api';
import {
    AllAuthenticationsTraffic,
    AuthenticationTypeSummary,
    EntityDetails,
    PolicyCountByStatus,
    PolicyType,
    ProtocolTypeSummary,
    RiskyEntity,
    RiskyIndicator
} from '../types';

export type AuthenticationFirewallMetricsResult = {
    protectedUsers?: number;
    protectedUsersIsLoading: boolean;
    protectedUsersIsError: boolean;
    denyPolicies?: PolicyCountByStatus;
    denyPoliciesIsLoading: boolean;
    denyPoliciesIsError: boolean;
    authenticationsDenied?: number;
    authenticationsDeniedIsLoading: boolean;
    authenticationsDeniedIsError: boolean;
    authenticationDenyRate?: number;
    authenticationDenyRateIsLoading: boolean;
    authenticationDenyRateIsError: boolean;
    authenticationTypeSummary?: AuthenticationTypeSummary;
    authenticationTypeSummaryIsLoading: boolean;
    authenticationTypeSummaryIsError: boolean;
    protocolTypeSummary?: ProtocolTypeSummary;
    protocolTypeSummaryIsLoading: boolean;
    protocolTypeSummaryIsError: boolean;
    policyType?: PolicyType;
    policyTypeIsLoading: boolean;
    policyTypeIsError: boolean;
    allAuthenticationsTraffic?: AllAuthenticationsTraffic[];
    allAuthenticationsTrafficIsLoading: boolean;
    allAuthenticationsTrafficIsError: boolean;
    activeUsers?: EntityDetails[];
    activeUsersIsLoading: boolean;
    activeUsersIsError: boolean;
    activeResources?: EntityDetails[];
    activeResourcesIsLoading: boolean;
    activeResourcesIsError: boolean;
    blockedResources?: EntityDetails[];
    blockedResourcesIsLoading: boolean;
    blockedResourcesIsError: boolean;
    blockedUsers?: EntityDetails[];
    blockedUsersIsLoading: boolean;
    blockedUsersIsError: boolean;
    riskyUsers?: RiskyEntity[];
    riskyUsersIsLoading: boolean;
    riskyUsersIsError: boolean;
    riskyResources?: RiskyEntity[];
    riskyResourcesIsLoading: boolean;
    riskyResourcesIsError: boolean;
    riskyIndicators?: RiskyIndicator[];
    riskyIndicatorsIsLoading: boolean;
    riskyIndicatorsIsError: boolean;
};

export const useAuthenticationFirewallMetrics = (filters: QueryFilters): AuthenticationFirewallMetricsResult => {
    const domainFilterOnly = { domain: filters.domain };

    const { data: denyPolicies, isLoading: denyPoliciesIsLoading, isError: denyPoliciesIsError } = useDenyPolicies();

    const { data: policyType, isLoading: policyTypeIsLoading, isError: policyTypeIsError } = usePolicyType();

    const {
        data: protectedUsers,
        isLoading: protectedUsersIsLoading,
        isError: protectedUsersIsError
    } = useProtectedUsers(domainFilterOnly);

    const {
        data: activeUsers,
        isLoading: activeUsersIsLoading,
        isError: activeUsersIsError
    } = useActiveUsersPreview(domainFilterOnly);

    const {
        data: activeResources,
        isLoading: activeResourcesIsLoading,
        isError: activeResourcesIsError
    } = useActiveResourcesPreview(domainFilterOnly);

    const {
        data: blockedResources,
        isLoading: blockedResourcesIsLoading,
        isError: blockedResourcesIsError
    } = useBlockedResourcesPreview(domainFilterOnly);

    const {
        data: blockedUsers,
        isLoading: blockedUsersIsLoading,
        isError: blockedUsersIsError
    } = useBlockedUsersPreview(domainFilterOnly);

    const {
        data: riskyUsers,
        isLoading: riskyUsersIsLoading,
        isError: riskyUsersIsError
    } = useRiskyUsers(domainFilterOnly);

    const {
        data: riskyResources,
        isLoading: riskyResourcesIsLoading,
        isError: riskyResourcesIsError
    } = useRiskyResources(domainFilterOnly);

    const {
        data: riskyIndicators,
        isLoading: riskyIndicatorsIsLoading,
        isError: riskyIndicatorsIsError
    } = useRiskyIndicators(domainFilterOnly);

    const {
        data: authenticationsDenied,
        isLoading: authenticationsDeniedIsLoading,
        isError: authenticationsDeniedIsError
    } = useAuthenticationsDenied(filters);

    const {
        data: authenticationDenyRate,
        isLoading: authenticationDenyRateIsLoading,
        isError: authenticationDenyRateIsError
    } = useAuthenticationDenyRate(filters);

    const {
        data: authenticationTypeSummary,
        isLoading: authenticationTypeSummaryIsLoading,
        isError: authenticationTypeSummaryIsError
    } = useAuthenticationTypeSummary(filters);

    const {
        data: protocolTypeSummary,
        isLoading: protocolTypeSummaryIsLoading,
        isError: protocolTypeSummaryIsError
    } = useProtocolTypeSummary(filters);

    const {
        data: allAuthenticationsTraffic,
        isLoading: allAuthenticationsTrafficIsLoading,
        isError: allAuthenticationsTrafficIsError
    } = useAllAuthenticationsTraffic(filters);

    return {
        protectedUsers,
        protectedUsersIsLoading,
        protectedUsersIsError,
        denyPolicies,
        denyPoliciesIsLoading,
        denyPoliciesIsError,
        authenticationsDenied,
        authenticationsDeniedIsLoading,
        authenticationsDeniedIsError,
        authenticationDenyRate,
        authenticationDenyRateIsLoading,
        authenticationDenyRateIsError,
        authenticationTypeSummary,
        authenticationTypeSummaryIsLoading,
        authenticationTypeSummaryIsError,
        protocolTypeSummary,
        protocolTypeSummaryIsLoading,
        protocolTypeSummaryIsError,
        policyType,
        policyTypeIsLoading,
        policyTypeIsError,
        allAuthenticationsTraffic,
        allAuthenticationsTrafficIsLoading,
        allAuthenticationsTrafficIsError,
        activeUsers,
        activeUsersIsLoading,
        activeUsersIsError,
        activeResources,
        activeResourcesIsLoading,
        activeResourcesIsError,
        blockedResources,
        blockedResourcesIsLoading,
        blockedResourcesIsError,
        blockedUsers,
        blockedUsersIsLoading,
        blockedUsersIsError,
        riskyUsers,
        riskyUsersIsLoading,
        riskyUsersIsError,
        riskyResources,
        riskyResourcesIsLoading,
        riskyResourcesIsError,
        riskyIndicators,
        riskyIndicatorsIsLoading,
        riskyIndicatorsIsError
    };
};
